import * as Yup from 'yup';
import {
    Autocomplete,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    FormHelperText,
    Grid,
    InputAdornment,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    Switch,
    TextField,
    Typography
} from '@mui/material';
import React, { useEffect, useMemo } from 'react';
import { OpenModalHookType } from '@hooks/useOpenModal';
import { Controller, SubmitHandler, useFieldArray, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import { useSelector } from 'react-redux';
import { RootReducer } from '@store/index';
import { formatUnitCost, Input } from '@api/Inputs/Input';
import { buildInputsById, getRecipeCost, Recipe } from '@api/Recipes/Recipe';
import { buildRecipesById, ModifierGroup } from '@api/ModifierGroups/ModifierGroup';
import {
    Dish,
    DishComponent,
    formatMargin,
    getDishCost,
    getDishCostSale,
    getDishGroupKey,
    getDishGroupLabel,
    getDishCostScenarios,
    DishModifierGroup
} from '@api/Dishes/Dish';
import { ProductCategory } from '@api/ProductCategories/ProductCategory';
import { PhotoUploader } from '@screens/Catalog/components/PhotoUploader';
import { CostSummaryBar } from '@screens/Catalog/components/CostSummaryBar';
import { EmptyItemsBox } from '@screens/Catalog/components/EmptyItemsBox';

interface ModalSaveDishProps {
    modalSaveDish: OpenModalHookType;
    dish?: Dish;
    recipesList: Recipe[];
    inputsList: Input[];
    modifierGroupsList: ModifierGroup[];
    productCategories: ProductCategory[];
    onSaveDish: (dish: Dish) => void;
}

const defaultDishValues: any = {
    id: undefined,
    name: '',
    cost: 0,
    productCategoryId: '',
    photoSrc: '',
    description: '',
    priority: 1,
    status: true,
    availableOnline: false,
    branchOfficesAvailable: ['all'],
    components: [],
    modifierGroups: []
};

const saveDishSchema = Yup.object({
    id: Yup.string().nullable(),
    name: Yup.string().required('Nombre requerido'),
    // ⚠️ **$0 es un precio VÁLIDO** (2026-08-29, pedido del negocio): promociones,
    // papas de cortesía por reseña, productos de regalo. Ya había cortesías en
    // producción hechas a mano — 4 pedidos con total $0 que la auditoría de
    // pagos confirmó como reales, no chatarra.
    //
    // Lo de aguas abajo ya lo soportaba: `getDishMargin` devuelve 0 con precio
    // ≤ 0 (no divide entre cero) y `getDishCostScenarios` marca `isGiveaway`
    // con precio ≤ $1 para informar el costo sin un porcentaje absurdo. Lo
    // único que lo impedía era esta validación.
    cost: Yup.number().typeError('Precio requerido').required('Precio requerido').min(0, 'No puede ser negativo'),
    // Obligatoria: el menú (v1 y 2.0) agrupa POR categoría — un producto sin ella
    // no se renderiza en ninguna sección, o sea desaparece del menú sin avisar
    productCategoryId: Yup.string().required('Categoría requerida'),
    photoSrc: Yup.string().nullable(),
    description: Yup.string().nullable(),
    priority: Yup.number().typeError('Prioridad requerida').required('Prioridad requerida'),
    status: Yup.boolean(),
    availableOnline: Yup.boolean(),
    branchOfficesAvailable: Yup.array().min(1, 'Debes de elegir al menos una sucursal').required('Sucursal requerida'),
    components: Yup.array()
        .of(
            Yup.object().shape({
                recipeId: Yup.string().required('Receta requerida'),
                quantity: Yup.number()
                    .typeError('Cantidad requerida')
                    .required('Cantidad requerida')
                    .moreThan(0, 'Debe ser mayor a 0')
            })
        )
        // Un combo "puro elección" puede no tener componentes fijos: todo su consumo
        // viene de los modificadores. Pero algo tiene que vender.
        .test('components-or-groups', 'Agrega al menos un componente o un modificador', function (value) {
            return (value?.length || 0) > 0 || ((this.parent.modifierGroups || []) as unknown[]).length > 0;
        }),
    modifierGroups: Yup.array().of(
        Yup.object().shape({
            groupId: Yup.string().required('Grupo requerido'),
            instanceId: Yup.string().nullable(), // lo genera saveDish; se conserva al editar
            label: Yup.string().nullable(), // nombre del grupo EN ESTE producto
            required: Yup.boolean(),
            min: Yup.number().typeError('Mínimo requerido').required('Mínimo requerido').min(0, 'Mínimo 0'),
            max: Yup.number()
                .typeError('Máximo requerido')
                .required('Máximo requerido')
                .min(1, 'Mínimo 1')
                .test('max-gte-min', 'Debe ser ≥ al mínimo', function (value) {
                    return (value ?? 0) >= (this.parent.min ?? 0);
                }),
            maxDistinctOptions: Yup.number().typeError('Número requerido').min(0, 'Mínimo 0'),
            maxFromGroupId: Yup.string().nullable(),
            allowQuantity: Yup.boolean()
        })
    )
});

export const ModalSaveDish: React.FunctionComponent<ModalSaveDishProps> = (props) => {
    const { modalSaveDish, dish, recipesList, inputsList, modifierGroupsList, productCategories, onSaveDish } = props;

    // Branch offices vienen del catálogo compartido; se agrega la opción "all" (patrón de ModalAddProduct)
    const { branchOffices: branchOfficesCatalog } = useSelector((state: RootReducer) => state.krispis);
    const branchOffices = useMemo(
        () => [...branchOfficesCatalog, { id: 'all', name: 'Todas las sucursales' }],
        [branchOfficesCatalog]
    );

    const inputsById = useMemo(() => buildInputsById(inputsList), [inputsList]);
    const recipesById = useMemo(() => buildRecipesById(recipesList), [recipesList]);
    const activeRecipes = useMemo(
        () => recipesList.filter((recipe) => recipe.status).sort((a, b) => a.name.localeCompare(b.name, 'es')),
        [recipesList]
    );
    const sortedGroups = useMemo(
        () => [...modifierGroupsList].sort((a, b) => a.name.localeCompare(b.name, 'es')),
        [modifierGroupsList]
    );

    const {
        control,
        handleSubmit,
        reset,
        watch,
        formState: { errors }
    } = useForm<Dish>({
        resolver: yupResolver(saveDishSchema),
        defaultValues: defaultDishValues
    });

    const {
        fields: componentFields,
        append: appendComponent,
        remove: removeComponent
    } = useFieldArray({
        control,
        name: 'components'
    });

    const {
        fields: groupFields,
        append: appendGroup,
        remove: removeGroup
    } = useFieldArray({
        control,
        name: 'modifierGroups'
    });

    // Costeo y margen en vivo mientras se captura
    const watchedComponents = (watch('components') || []) as DishComponent[];
    const watchedPrice = Number(watch('cost')) || 0;
    const liveCost = getDishCost(watchedComponents, recipesById, inputsById);
    const liveCostSale = getDishCostSale(watchedComponents, recipesById, inputsById);
    // Escenarios (F7): sumar solo los componentes miente en los productos que
    // consumen por modificadores. El rango se recalcula mientras se captura, así
    // que quien arma el producto ve de inmediato el efecto de cada grupo.
    const watchedGroups = (watch('modifierGroups') || []) as DishModifierGroup[];
    const modifierGroupsById = useMemo(
        () =>
            new Map(
                (modifierGroupsList || [])
                    .filter((group) => group.id)
                    .map((group) => [group.id as string, group])
            ),
        [modifierGroupsList]
    );
    const liveScenarios = getDishCostScenarios(
        { cost: watchedPrice, components: watchedComponents, modifierGroups: watchedGroups },
        modifierGroupsById,
        recipesById,
        inputsById
    );

    const addComponent = () => appendComponent({ recipeId: '', quantity: 1 });
    const addGroup = () =>
        appendGroup({
            groupId: '',
            // El instanceId lo genera saveDish al guardar (id estable de Firestore).
            // Mientras tanto el renglón se identifica por su índice en el formulario.
            instanceId: '',
            label: '',
            required: false,
            min: 0,
            max: 1,
            allowQuantity: false,
            maxDistinctOptions: 0,
            maxFromGroupId: ''
        });

    const handleClose = () => {
        reset(defaultDishValues);
        modalSaveDish.toggle();
    };

    const onSaveDishForm: SubmitHandler<Dish> = (values) => {
        onSaveDish(values);
    };

    useEffect(() => {
        if (modalSaveDish.modalOpen) {
            if (dish && dish.id) {
                reset({
                    id: dish.id,
                    name: dish.name || '',
                    cost: dish.cost ?? 0,
                    productCategoryId: dish.productCategoryId || '',
                    photoSrc: dish.photoSrc || '',
                    description: dish.description || '',
                    priority: dish.priority || 1,
                    status: dish.status ?? true,
                    availableOnline: dish.availableOnline ?? false,
                    branchOfficesAvailable: dish.branchOfficesAvailable?.length ? dish.branchOfficesAvailable : ['all'],
                    components: dish.components
                        ? dish.components.map((component) => ({
                              recipeId: component.recipeId,
                              quantity: component.quantity || 0
                          }))
                        : [],
                    modifierGroups: dish.modifierGroups
                        ? dish.modifierGroups.map((group) => ({
                              groupId: group.groupId,
                              instanceId: group.instanceId || '',
                              label: group.label || '',
                              required: group.required ?? false,
                              min: group.min ?? 0,
                              max: group.max ?? 1,
                              allowQuantity: group.allowQuantity ?? false,
                              maxDistinctOptions: group.maxDistinctOptions ?? 0,
                              maxFromGroupId: group.maxFromGroupId || ''
                          }))
                        : []
                });
            } else {
                reset(defaultDishValues);
            }
        }
    }, [modalSaveDish.modalOpen, dish, reset]);

    return (
        <Dialog
            PaperProps={{
                style: { borderRadius: 15, padding: 4 }
            }}
            open={modalSaveDish.modalOpen}
            onClose={handleClose}
            aria-labelledby="modal-save-dish-title"
            fullWidth
            maxWidth="lg"
        >
            <DialogTitle id="modal-save-dish-title">
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                    <Box>
                        <Typography variant="h6" fontWeight={600}>
                            {watch('id') ? 'Editar producto' : 'Nuevo producto'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Precio, componentes (recetas) y modificadores del vendible del menú 2.0
                        </Typography>
                    </Box>
                    <IconButton color="secondary" onClick={handleClose}>
                        <CloseIcon />
                    </IconButton>
                </Stack>
            </DialogTitle>
            <DialogContent>
                <form id="save-dish-form" onSubmit={handleSubmit(onSaveDishForm)}>
                    <Grid container spacing={2} pt={1}>
                        {/* FOTO + DATOS GENERALES */}
                        <Grid item xs={12} md={3}>
                            <Controller
                                name="photoSrc"
                                control={control}
                                render={({ field: { onChange, value } }) => (
                                    <PhotoUploader value={value} onChange={onChange} />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} md={9}>
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={6}>
                                    <Controller
                                        name="name"
                                        control={control}
                                        render={({ field }) => (
                                            <TextField
                                                {...field}
                                                fullWidth
                                                required
                                                label="Nombre"
                                                error={!!errors.name}
                                                helperText={errors.name?.message}
                                            />
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={6} md={3}>
                                    <Controller
                                        name="cost"
                                        control={control}
                                        render={({ field }) => (
                                            <TextField
                                                {...field}
                                                fullWidth
                                                required
                                                type="number"
                                                label="Precio de venta"
                                                inputProps={{ step: 'any', min: 0 }}
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment position="start">$</InputAdornment>
                                                    )
                                                }}
                                                error={!!errors.cost}
                                                helperText={errors.cost?.message}
                                            />
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={6} md={3}>
                                    <Controller
                                        name="priority"
                                        control={control}
                                        render={({ field }) => (
                                            <TextField
                                                {...field}
                                                fullWidth
                                                required
                                                label="Prioridad"
                                                type="number"
                                                error={!!errors.priority}
                                                helperText={errors.priority?.message || 'Orden en el menú'}
                                            />
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <FormControl fullWidth required error={!!errors.productCategoryId}>
                                        <InputLabel id="dish-cat-label">Categoría</InputLabel>
                                        <Controller
                                            name="productCategoryId"
                                            control={control}
                                            render={({ field }) => (
                                                <Select
                                                    {...field}
                                                    labelId="dish-cat-label"
                                                    label="Categoría"
                                                    value={field.value || ''}
                                                >
                                                    {productCategories.map((category) => (
                                                        <MenuItem key={category.id} value={category.id}>
                                                            {category.name}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            )}
                                        />
                                        <FormHelperText>
                                            {errors.productCategoryId?.message ||
                                                'El menú agrupa por categoría: sin ella el producto no aparece'}
                                        </FormHelperText>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <FormControl fullWidth error={!!errors.branchOfficesAvailable}>
                                        <InputLabel id="dish-branch-label">Disponible en</InputLabel>
                                        <Controller
                                            name="branchOfficesAvailable"
                                            control={control}
                                            render={({ field }) => (
                                                <Select
                                                    {...field}
                                                    labelId="dish-branch-label"
                                                    multiple
                                                    label="Disponible en"
                                                    value={Array.isArray(field.value) ? field.value : []}
                                                >
                                                    {branchOffices.map((branchOffice) => (
                                                        <MenuItem key={branchOffice.id} value={branchOffice.id}>
                                                            {branchOffice.name}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            )}
                                        />
                                        {errors.branchOfficesAvailable && (
                                            <FormHelperText>{errors.branchOfficesAvailable.message}</FormHelperText>
                                        )}
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Controller
                                        name="description"
                                        control={control}
                                        render={({ field }) => (
                                            <TextField {...field} fullWidth label="Descripción (visible online)" />
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Stack
                                        direction="row"
                                        spacing={2}
                                        alignItems="center"
                                        height="100%"
                                        justifyContent={{ xs: 'flex-start', md: 'flex-end' }}
                                    >
                                        <Controller
                                            name="availableOnline"
                                            control={control}
                                            render={({ field: { onChange, value } }) => (
                                                <FormControlLabel
                                                    control={<Switch checked={!!value} onChange={onChange} />}
                                                    label="Disponible en línea"
                                                />
                                            )}
                                        />
                                        <Controller
                                            name="status"
                                            control={control}
                                            render={({ field: { onChange, value } }) => (
                                                <FormControlLabel
                                                    control={<Switch checked={!!value} onChange={onChange} />}
                                                    label="Activo"
                                                />
                                            )}
                                        />
                                    </Stack>
                                </Grid>
                            </Grid>
                        </Grid>

                        {/* COMPONENTES (recetas base) */}
                        <Grid item xs={12}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center" mt={1}>
                                <Typography fontSize={17} fontWeight={600}>
                                    Componentes (recetas que consume)
                                </Typography>
                                {componentFields.length > 0 && (
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        startIcon={<AddIcon />}
                                        onClick={addComponent}
                                    >
                                        Agregar componente
                                    </Button>
                                )}
                            </Stack>
                            {errors.components && !Array.isArray(errors.components) && (
                                <FormHelperText error>{(errors.components as any)?.message}</FormHelperText>
                            )}
                        </Grid>
                        <Grid item xs={12}>
                            {componentFields.length === 0 && (
                                <EmptyItemsBox
                                    text="Sin componentes fijos (válido si todo lo que consume viene de los modificadores — ej. combos de elección)"
                                    buttonLabel="Agregar componente"
                                    onAdd={addComponent}
                                />
                            )}
                            {componentFields.map((field, index) => {
                                const currentRecipeId = watch(`components.${index}.recipeId`);
                                const currentRecipe = currentRecipeId ? recipesById.get(currentRecipeId) : undefined;
                                const missingRecipe = !!currentRecipeId && !currentRecipe;
                                const quantity = Number(watch(`components.${index}.quantity`)) || 0;
                                const rowCost = currentRecipe
                                    ? quantity * getRecipeCost(currentRecipe.items, inputsById)
                                    : 0;
                                const options: Recipe[] = missingRecipe
                                    ? [
                                          {
                                              id: currentRecipeId,
                                              name: `Receta eliminada (${currentRecipeId})`
                                          } as Recipe,
                                          ...activeRecipes
                                      ]
                                    : activeRecipes;
                                const valueOption = options.find((option) => option.id === currentRecipeId) || null;

                                return (
                                    <Paper key={field.id} variant="outlined" sx={{ p: 1.5, borderRadius: 2, mb: 1.5 }}>
                                        <Grid container spacing={2} alignItems="center">
                                            <Grid item xs={12} md={6}>
                                                <Controller
                                                    name={`components.${index}.recipeId`}
                                                    control={control}
                                                    render={({ field: { onChange } }) => (
                                                        <Autocomplete
                                                            options={options}
                                                            value={valueOption}
                                                            onChange={(_, option) => onChange(option?.id || '')}
                                                            getOptionLabel={(option) => option.name}
                                                            isOptionEqualToValue={(a, b) => a.id === b.id}
                                                            noOptionsText="Sin recetas activas"
                                                            renderOption={(props, option) => (
                                                                <li {...props} key={option.id}>
                                                                    <Stack>
                                                                        <Typography variant="body2">
                                                                            {option.name}
                                                                        </Typography>
                                                                        {option.items && (
                                                                            <Typography
                                                                                variant="caption"
                                                                                color="text.secondary"
                                                                            >
                                                                                {formatUnitCost(
                                                                                    getRecipeCost(
                                                                                        option.items,
                                                                                        inputsById
                                                                                    )
                                                                                )}{' '}
                                                                                de insumos
                                                                            </Typography>
                                                                        )}
                                                                    </Stack>
                                                                </li>
                                                            )}
                                                            renderInput={(params) => (
                                                                <TextField
                                                                    {...params}
                                                                    label="Receta"
                                                                    required
                                                                    error={
                                                                        !!errors.components?.[index]?.recipeId ||
                                                                        missingRecipe
                                                                    }
                                                                    helperText={
                                                                        errors.components?.[index]?.recipeId
                                                                            ?.message ||
                                                                        (missingRecipe &&
                                                                            'Esta receta ya no existe: elige una vigente')
                                                                    }
                                                                />
                                                            )}
                                                        />
                                                    )}
                                                />
                                            </Grid>
                                            <Grid item xs={8} md={4}>
                                                <Controller
                                                    name={`components.${index}.quantity`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <TextField
                                                            {...field}
                                                            fullWidth
                                                            required
                                                            type="number"
                                                            label="Recetas por producto"
                                                            inputProps={{ step: 'any', min: 0 }}
                                                            error={!!errors.components?.[index]?.quantity}
                                                            helperText={errors.components?.[index]?.quantity?.message}
                                                        />
                                                    )}
                                                />
                                            </Grid>
                                            <Grid item xs={4} md={2}>
                                                <Stack
                                                    direction={{ xs: 'row', md: 'column' }}
                                                    alignItems={{ xs: 'center', md: 'flex-end' }}
                                                    justifyContent="space-between"
                                                    spacing={0.5}
                                                >
                                                    <Typography variant="body2" fontWeight={700}>
                                                        {formatUnitCost(rowCost)}
                                                    </Typography>
                                                    <IconButton
                                                        size="small"
                                                        color="error"
                                                        onClick={() => removeComponent(index)}
                                                    >
                                                        <DeleteOutlineIcon fontSize="small" />
                                                    </IconButton>
                                                </Stack>
                                            </Grid>
                                        </Grid>
                                    </Paper>
                                );
                            })}
                        </Grid>

                        {/* GRUPOS DE MODIFICADORES */}
                        <Grid item xs={12}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center" mt={1}>
                                <Typography fontSize={17} fontWeight={600}>
                                    Modificadores del producto
                                </Typography>
                                {groupFields.length > 0 && (
                                    <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={addGroup}>
                                        Agregar modificador
                                    </Button>
                                )}
                            </Stack>
                        </Grid>
                        <Grid item xs={12}>
                            {groupFields.length === 0 && (
                                <EmptyItemsBox
                                    text="Sin modificadores: el producto se vende tal cual (opcional)"
                                    buttonLabel="Agregar modificador"
                                    onAdd={addGroup}
                                />
                            )}
                            {groupFields.map((field, index) => {
                                const currentGroupId = watch(`modifierGroups.${index}.groupId`);
                                const currentGroup = modifierGroupsList.find((group) => group.id === currentGroupId);
                                const missingGroup = !!currentGroupId && !currentGroup;
                                // Otros renglones del producto, para ligar el máximo ("1 sabor por pieza").
                                // Se identifican por su CLAVE (instancia) y se muestran con su etiqueta:
                                // el mismo modificador puede estar en varios renglones.
                                const allRows = watch('modifierGroups') || [];
                                const otherGroups = allRows
                                    .map((row, rowIndex) => ({ row, rowIndex }))
                                    .filter(({ row, rowIndex }) => rowIndex !== index && row.groupId)
                                    .map(({ row, rowIndex }) => {
                                        const rowGroup = modifierGroupsList.find((mg) => mg.id === row.groupId);
                                        const name = getDishGroupLabel(row, rowGroup) || row.groupId;
                                        const repeated =
                                            allRows.filter((other) => other.groupId === row.groupId).length > 1;
                                        return {
                                            id: getDishGroupKey(row),
                                            name: !row.label && repeated ? `${name} (renglón ${rowIndex + 1})` : name
                                        };
                                    });
                                const options: ModifierGroup[] = missingGroup
                                    ? [
                                          {
                                              id: currentGroupId,
                                              name: `Modificador eliminado (${currentGroupId})`,
                                              options: []
                                          } as ModifierGroup,
                                          ...sortedGroups
                                      ]
                                    : sortedGroups;
                                const valueOption = options.find((option) => option.id === currentGroupId) || null;

                                return (
                                    <Paper key={field.id} variant="outlined" sx={{ p: 1.5, borderRadius: 2, mb: 1.5 }}>
                                        <Grid container spacing={2} alignItems="center">
                                            <Grid item xs={12} md={3}>
                                                <Controller
                                                    name={`modifierGroups.${index}.groupId`}
                                                    control={control}
                                                    render={({ field: { onChange } }) => (
                                                        <Autocomplete
                                                            options={options}
                                                            value={valueOption}
                                                            onChange={(_, option) => onChange(option?.id || '')}
                                                            getOptionLabel={(option) => option.name}
                                                            isOptionEqualToValue={(a, b) => a.id === b.id}
                                                            noOptionsText="Sin modificadores creados"
                                                            renderOption={(props, option) => (
                                                                <li {...props} key={option.id}>
                                                                    <Stack>
                                                                        <Typography variant="body2">
                                                                            {option.name}
                                                                        </Typography>
                                                                        <Typography
                                                                            variant="caption"
                                                                            color="text.secondary"
                                                                        >
                                                                            {(option.options || [])
                                                                                .map((o) => o.name)
                                                                                .join(', ') || 'Sin opciones'}
                                                                        </Typography>
                                                                    </Stack>
                                                                </li>
                                                            )}
                                                            renderInput={(params) => (
                                                                <TextField
                                                                    {...params}
                                                                    label="Modificador"
                                                                    required
                                                                    error={
                                                                        !!errors.modifierGroups?.[index]?.groupId ||
                                                                        missingGroup
                                                                    }
                                                                    helperText={
                                                                        errors.modifierGroups?.[index]?.groupId
                                                                            ?.message ||
                                                                        (missingGroup &&
                                                                            'Este modificador ya no existe: elige uno vigente')
                                                                    }
                                                                />
                                                            )}
                                                        />
                                                    )}
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={3}>
                                                {/* Etiqueta por producto: permite reutilizar UN modificador en
                                                    varios renglones ("Queso — Hamburguesa 1/2") en vez de crear
                                                    grupos casi-duplicados. Va al ticket y al tablero. */}
                                                <Controller
                                                    name={`modifierGroups.${index}.label`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <TextField
                                                            {...field}
                                                            fullWidth
                                                            value={field.value || ''}
                                                            label="Nombre en este producto"
                                                            placeholder={currentGroup?.name || 'Opcional'}
                                                            helperText="Opcional — para repetir el mismo modificador (ej. Hamburguesa 1)"
                                                        />
                                                    )}
                                                />
                                            </Grid>
                                            <Grid item xs={6} md={2}>
                                                <Controller
                                                    name={`modifierGroups.${index}.min`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <TextField
                                                            {...field}
                                                            fullWidth
                                                            required
                                                            type="number"
                                                            label="Mínimo"
                                                            inputProps={{ step: 1, min: 0 }}
                                                            error={!!errors.modifierGroups?.[index]?.min}
                                                            helperText={
                                                                errors.modifierGroups?.[index]?.min?.message ||
                                                                '0 = opcional'
                                                            }
                                                        />
                                                    )}
                                                />
                                            </Grid>
                                            <Grid item xs={6} md={2}>
                                                <Controller
                                                    name={`modifierGroups.${index}.max`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <TextField
                                                            {...field}
                                                            fullWidth
                                                            required
                                                            type="number"
                                                            label="Máximo"
                                                            inputProps={{ step: 1, min: 1 }}
                                                            error={!!errors.modifierGroups?.[index]?.max}
                                                            helperText={errors.modifierGroups?.[index]?.max?.message}
                                                        />
                                                    )}
                                                />
                                            </Grid>
                                            <Grid item xs={6} md={2}>
                                                <Controller
                                                    name={`modifierGroups.${index}.maxDistinctOptions`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <TextField
                                                            {...field}
                                                            fullWidth
                                                            type="number"
                                                            label="Máx. distintas"
                                                            inputProps={{ step: 1, min: 0 }}
                                                            error={!!errors.modifierGroups?.[index]?.maxDistinctOptions}
                                                            helperText={
                                                                errors.modifierGroups?.[index]?.maxDistinctOptions
                                                                    ?.message || '0 = sin límite'
                                                            }
                                                        />
                                                    )}
                                                />
                                            </Grid>
                                            <Grid item xs={6} md={3}>
                                                <FormControl fullWidth>
                                                    <InputLabel id={`max-from-label-${index}`}>
                                                        Máx. ligado a
                                                    </InputLabel>
                                                    <Controller
                                                        name={`modifierGroups.${index}.maxFromGroupId`}
                                                        control={control}
                                                        render={({ field }) => (
                                                            <Select
                                                                {...field}
                                                                labelId={`max-from-label-${index}`}
                                                                label="Máx. ligado a"
                                                                value={field.value || ''}
                                                            >
                                                                <MenuItem value="">
                                                                    <em>Máximo fijo</em>
                                                                </MenuItem>
                                                                {otherGroups.map((other) => (
                                                                    <MenuItem key={other.id} value={other.id}>
                                                                        {other.name}
                                                                    </MenuItem>
                                                                ))}
                                                            </Select>
                                                        )}
                                                    />
                                                    <FormHelperText>
                                                        1 por pieza del grupo elegido (ej. sabores ↔ piezas)
                                                    </FormHelperText>
                                                </FormControl>
                                            </Grid>
                                            <Grid item xs={12} md={3}>
                                                <Stack
                                                    direction="row"
                                                    alignItems="center"
                                                    justifyContent={{ xs: 'space-between', md: 'flex-end' }}
                                                    spacing={0.5}
                                                >
                                                    <Controller
                                                        name={`modifierGroups.${index}.required`}
                                                        control={control}
                                                        render={({ field: { onChange, value } }) => (
                                                            <FormControlLabel
                                                                control={
                                                                    <Switch
                                                                        size="small"
                                                                        checked={!!value}
                                                                        onChange={onChange}
                                                                    />
                                                                }
                                                                label={
                                                                    <Typography variant="body2">Obligatorio</Typography>
                                                                }
                                                            />
                                                        )}
                                                    />
                                                    <Controller
                                                        name={`modifierGroups.${index}.allowQuantity`}
                                                        control={control}
                                                        render={({ field: { onChange, value } }) => (
                                                            <FormControlLabel
                                                                control={
                                                                    <Switch
                                                                        size="small"
                                                                        checked={!!value}
                                                                        onChange={onChange}
                                                                    />
                                                                }
                                                                label={
                                                                    <Typography variant="body2">
                                                                        Permite repetir
                                                                    </Typography>
                                                                }
                                                            />
                                                        )}
                                                    />
                                                    <IconButton
                                                        size="small"
                                                        color="error"
                                                        onClick={() => removeGroup(index)}
                                                    >
                                                        <DeleteOutlineIcon fontSize="small" />
                                                    </IconButton>
                                                </Stack>
                                            </Grid>
                                        </Grid>
                                    </Paper>
                                );
                            })}
                        </Grid>
                    </Grid>
                </form>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Stack width="100%" spacing={1.5}>
                    <CostSummaryBar
                        items={[
                            { label: 'Costo fijo (compra)', value: formatUnitCost(liveCost) },
                            { label: '(traspaso)', value: formatUnitCost(liveCostSale) },
                            {
                                // Con modificadores el costo es un RANGO: mínimo
                                // obligatorio con lo barato → máximo con lo caro
                                label: liveScenarios.isFixed ? 'Costo total' : 'Costo con extras',
                                value: liveScenarios.isFixed
                                    ? formatUnitCost(liveScenarios.best.cost)
                                    : `${formatUnitCost(liveScenarios.best.cost)} – ${formatUnitCost(
                                          liveScenarios.worst.cost
                                      )}`
                            },
                            {
                                label: liveScenarios.isFixed ? 'Margen' : 'Margen real',
                                value: (() => {
                                    if (liveScenarios.isGiveaway) {
                                        return 'Regalo';
                                    }
                                    const peor = liveScenarios.worst.margin;
                                    const mejor = liveScenarios.best.margin;
                                    if (peor === undefined || mejor === undefined) {
                                        return '—';
                                    }
                                    return liveScenarios.isFixed
                                        ? formatMargin(mejor)
                                        : `${formatMargin(peor)} – ${formatMargin(mejor)}`;
                                })(),
                                color:
                                    !liveScenarios.isGiveaway && (liveScenarios.worst.margin ?? 0) < 0
                                        ? 'error.main'
                                        : undefined
                            }
                        ]}
                    />
                    <Box textAlign="center">
                        <Button
                            sx={{ width: 300 }}
                            color="primary"
                            variant="contained"
                            type="submit"
                            form="save-dish-form"
                        >
                            Guardar
                        </Button>
                    </Box>
                </Stack>
            </DialogActions>
        </Dialog>
    );
};
